import { LDFlagSet } from 'launchdarkly-react-client-sdk';
import { memoize } from 'micro-memoize';

import { ApiView, ApiViewState, getDefaultApiViewState } from '../../../core/ApiView';
import { currentUserQuery, CurrentUserResult } from '../../../queries/currentUser';
import { ExtendedWorkspaceListItem, processWorkspaces } from '../../../operations/workspaces/processors/workspacesList';
import { getUpdatedWorkspacesListItems } from '../../../operations/workspaces/utils/utils';
import { integrationsQuery } from '../../../queries/integrations';
import { queryClient } from '../../../core/queryClient';
import { workspaceOrgDatasourcesInSyncing } from '../../../operations/workspaces/processors/workspaceOrgsInSyncing';
import {
  workspacesDatasourcePollDataQuery,
  workspacesDatasourcePollDataQueryConfig
} from '../../../operations/workspaces/queries/workspacesDatasourcePollDataQuery';
import {
  workspacesListingQuery,
  workspacesListingQueryKey
} from '../../../operations/workspaces/queries/workspacesListingQuery';

const EMPTY_CONSOLIDATIONS: [] = [];

export interface WorkspacesListViewState extends ApiViewState {
  currentUser?: CurrentUserResult;
  workspaces?: ExtendedWorkspaceListItem[];
  workspacesError?: Error | null;
}

export class WorkspacesListView extends ApiView {
  protected currentUserObs: ReturnType<typeof currentUserQuery>;
  protected integrationsObs: ReturnType<typeof integrationsQuery>;
  protected workspacesObs: ReturnType<typeof workspacesListingQuery>;
  protected workspacesDSPollDataObs: ReturnType<typeof workspacesDatasourcePollDataQuery>;

  protected appliedPollDataKey = '';
  protected loadedPollDatasourceIdsKey = '';
  protected memoizedProcessWorkspaces: typeof processWorkspaces;
  protected memoizedOrgsInSyncing: typeof workspaceOrgDatasourcesInSyncing;

  public state: WorkspacesListViewState = {
    ...getDefaultApiViewState(),
    workspaces: []
  };

  constructor(protected flags: LDFlagSet, protected pollingEnabled = false) {
    super();

    // Observers
    this.currentUserObs = this.addQuery(currentUserQuery());
    this.integrationsObs = this.addQuery(integrationsQuery());
    this.workspacesObs = this.addQuery(workspacesListingQuery());
    this.workspacesDSPollDataObs = this.addQuery(workspacesDatasourcePollDataQuery([]));

    this.memoizedOrgsInSyncing = memoize(workspaceOrgDatasourcesInSyncing);
    this.memoizedProcessWorkspaces = memoize(processWorkspaces);

    // useApiView starts subscriptions after mounting. A render may be discarded,
    // so constructing the view must not start requests or polling.
  }

  // ***************
  // *  Public API *
  // ***************

  public getWorkspace(workspaceId: string) {
    return this.processedworkspaces?.find(w => w.id === workspaceId);
  }

  public get isLoading() {
    return [this.currentUserObs, this.integrationsObs, this.workspacesObs].some(
      observer => observer.getCurrentResult().isPending
    );
  }

  // ****************************
  // *  Private / Protected API *
  // ****************************
  protected get currentUser() {
    return this.getResult(this.currentUserObs).data;
  }

  protected get datasourceIDsInSyncing() {
    return this.memoizedOrgsInSyncing(this.rawWorkspaces);
  }

  protected get integrations() {
    return this.getResult(this.integrationsObs).data;
  }

  protected get rawWorkspaces() {
    return this.getResult(this.workspacesObs).data;
  }

  protected get processedworkspaces() {
    const rawWorkspaces = this.rawWorkspaces;
    const integrations = this.integrations;
    const currentUser = this.currentUser;
    if (!rawWorkspaces || !integrations || !currentUser) {
      return undefined;
    }
    return this.memoizedProcessWorkspaces(rawWorkspaces, integrations, currentUser, EMPTY_CONSOLIDATIONS, this.flags);
  }

  protected get workspacesError() {
    return this.getResult(this.workspacesObs).error;
  }

  protected get workspacesDSPollData() {
    return this.getResult(this.workspacesDSPollDataObs).data;
  }

  protected updateDependentQueries() {
    if (this.pollingEnabled) {
      // Update workspaces with latest poll data
      this.updateWorkspacesFromPollData();

      // Update the observer for polling based on current orgs in syncing
      this.updateWorkspacePollDataObserver();
    }
  }

  protected updateWorkspacesFromPollData() {
    const pollData = this.workspacesDSPollData;
    const rawWorkspaces = this.rawWorkspaces;

    if (!pollData?.length || !rawWorkspaces?.length) {
      return;
    }

    const pollDataKey = pollData
      .map(item => `${item.id}:${item.sync_status}:${item.sync_status_modified}`)
      .sort()
      .join('|');

    if (pollDataKey !== this.appliedPollDataKey) {
      // Update each workspace's organisations with matching poll data
      const updatedWorkspaces = getUpdatedWorkspacesListItems(rawWorkspaces, pollData);

      // Persist updated workspaces to React Query cache
      queryClient.setQueryData(workspacesListingQueryKey(), updatedWorkspaces);
      this.appliedPollDataKey = pollDataKey;
    }
  }

  protected updateWorkspacePollDataObserver() {
    const datasourceIds = this.datasourceIDsInSyncing;
    const datasourceIdsKey = datasourceIds.join(',');

    if (datasourceIdsKey === this.loadedPollDatasourceIdsKey) {
      return;
    }

    this.workspacesDSPollDataObs.setOptions(workspacesDatasourcePollDataQueryConfig(datasourceIds));
    this.loadedPollDatasourceIdsKey = datasourceIdsKey;
  }

  protected getNewState(): WorkspacesListViewState {
    return {
      ...super.getNewState(),
      currentUser: this.currentUser,
      workspaces: this.processedworkspaces,
      workspacesError: this.workspacesError
    };
  }
}
